import { Input, Pagination, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { customersKey, useCustomers, type Customer } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { absent, formatDate } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';
import type { Session } from '@core/session/types';

import { CustomerDrawer, type Editing } from './customers/CustomerDrawer';

const pageSize = 25;

/**
 * The customer master (spec Section 12).
 *
 * <p>
 * Built ahead of Section 8's rental orders, because an order has to belong to somebody. The screen
 * is shaped around the one fact that identifies a rental customer: <strong>the mobile number.</strong>
 * Nobody at a counter asks for a customer code, they ask for a phone number, so it is the first
 * column, the first field, and what the search matches first — typed with spaces or without, since
 * the server normalises both ends.
 * </p>
 *
 * <p>
 * What is deliberately not here: Section 12's versioned measurements, fitting appointments and
 * group bookings, and Section 13's risk score and blacklist. Those are Phase 3 and their own
 * tables. The KYC fields <em>are</em> here, because a booking captures an ID at the counter and
 * there is nowhere else to put it.
 * </p>
 */
export function CustomersPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);

  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, includeInactive]);

  const customers = useCustomers(session.branch?.id, {
    search: debouncedSearch,
    includeInactive,
    page,
    pageSize,
  });

  const remove = useApiMutation<void, Customer>({
    mutationFn: (customer) => apiFetch<void>(`/api/customer/${customer.id}`, { method: 'DELETE' }),
    success: (_result, customer) => `${customer.name} deleted.`,
    invalidates: [customersKey],
  });

  const columns: DataViewColumn<Customer>[] = [
    {
      title: 'Customer',
      dataIndex: 'name',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.name}</span>
            {/* Only the exceptions are marked, as on every other master: shared is the ordinary
                case for a customer, and branch-local is what an operator needs to spot. */}
            {!row.isShared && <StatusBadge tone="brand">This branch</StatusBadge>}
            {!row.isActive && <StatusBadge tone="warning">Inactive</StatusBadge>}
            {row.gstin && <StatusBadge tone="neutral">GST</StatusBadge>}
          </div>
          <span className="font-mono-nums text-[12px] text-default">
            {row.mobile}
            {row.city ? ` · ${row.city}` : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'ID on file',
      key: 'id',
      width: 190,
      responsive: ['lg'],
      render: (_, row) =>
        row.idDocumentType === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px]">{humanizeEnum(row.idDocumentType)}</span>
            {row.idDocumentExpiresOn && (
              // Expiry decided by the server, not by the browser's clock — see the DTO.
              <span
                className={`text-[11px] ${row.idDocumentIsExpired ? 'text-danger' : 'text-default'}`}
              >
                {row.idDocumentIsExpired ? 'expired ' : 'expires '}
                {formatDate(row.idDocumentExpiresOn)}
              </span>
            )}
          </div>
        ),
    },
    {
      title: 'Rentals',
      dataIndex: 'rentalCount',
      width: 110,
      align: 'right',
      responsive: ['xl'],
      render: (_, row) => (
        <div className="flex flex-col items-end">
          <span className="font-mono-nums">{row.rentalCount}</span>
          {row.lastRentedOn && (
            <span className="text-[11px] text-default">{formatDate(row.lastRentedOn)}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Spend',
      dataIndex: 'totalSpend',
      width: 120,
      align: 'right',
      responsive: ['xl'],
      hideOnCard: true,
      render: (_, row) =>
        row.totalSpend === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums">{row.totalSpend.toLocaleString('en-IN')}</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="Customer:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', customer: row })}
              />
            )}
          </WriteAction>

          <RecordAction session={session} required="Customer:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this customer?"
                description="Only for someone entered by mistake. If they simply stopped coming, edit them and switch them inactive instead — that keeps their rental history."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (customers.isError) {
    return <ApiErrorAlert error={customers.error} />;
  }

  const total = customers.data?.total ?? 0;

  return (
    <>
      <PageHeading
        title="Customers"
        subtitle="Who rents from the shop. A mobile number is what identifies one."
        actions={
          <WriteAction session={session} required="Customer:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New customer
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search mobile, name or email"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default sm:ms-auto">
            <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
            Show inactive
          </label>
        </div>

        <DataView<Customer>
          columns={columns}
          dataSource={customers.data?.items ?? []}
          loading={customers.isLoading}
          empty={
            <EmptyState
              icon="icon icon-contact"
              title={debouncedSearch ? 'Nobody matches that' : 'No customers yet'}
              hint={
                debouncedSearch
                  ? 'Try part of the number instead — the search matches it with or without spaces.'
                  : 'Add the first one, or they will be created as bookings are taken.'
              }
            />
          }
        />

        {total > pageSize && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} customers`}
            />
          </div>
        )}
      </Card>

      {editing && <CustomerDrawer editing={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
